import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Helmet } from 'react-helmet';

const GRADE_OPTIONS = [
  { value: '', label: 'Select Grade' },
  { value: 'S-30', label: 'S-30' },
  { value: 'M-30', label: 'M-30' },
  { value: 'SS-30', label: 'SS-30' },
  { value: 'S1-30', label: 'S1-30' },
  { value: 'S2-30', label: 'S2-30' },
  { value: 'S-31', label: 'S-31' },
  { value: 'Refined', label: 'Refined' },
  { value: 'Other', label: 'Other' },
];

const QUALITY_OPTIONS = [
  { value: '', label: 'Select Quality' },
  { value: 'Best', label: 'Best' },
  { value: 'Good', label: 'Good' },
  { value: 'Slightly Discoloured', label: 'Slightly Discoloured' },
  { value: 'High Moisture', label: 'High Moisture' },
  { value: 'Damaged Bags', label: 'Damaged Bags' },
  { value: 'Other', label: 'Other' },
];

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

async function sendSellOfferEmail(eventType, sellerEmail, offerData) {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/send-sell-offer-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ eventType, sellerEmail, offerData }),
    });
  } catch (err) {
    // Non-blocking — email failure should not block the UI action
    console.error('Email notification error:', err);
  }
}

const SellYourSugar = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { t } = useTranslation();
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [showGuestSubmitPrompt, setShowGuestSubmitPrompt] = useState(false);

  const [form, setForm] = useState({
    boughtFromFactory: '',
    factoryLocation: '',
    loadingPincode: '',
    grade: '',
    season: '',
    quantity: '',
    offeredRate: '',
    paymentByDate: '',
    liftingByDate: '',
    offerValidity: '',
    qualityRemark: '',
    qualityNote: '',
    contactMobile: userProfile?.mobile || user?.phone || '',
  });

  const [photos, setPhotos] = useState([]); // max 3
  const [videos, setVideos] = useState([]); // at least 1 mandatory
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhotoAdd = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;
    const remaining = 3 - photos?.length;
    const toAdd = files?.slice(0, remaining)?.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file?.name,
    }));
    setPhotos(prev => [...prev, ...toAdd]);
    if (errors?.photos) setErrors(prev => ({ ...prev, photos: '' }));
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated?.[idx]?.preview);
      updated?.splice(idx, 1);
      return updated;
    });
  };

  const handleVideoAdd = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;
    const toAdd = files?.map(file => ({
      file,
      name: file?.name,
      size: (file?.size / (1024 * 1024))?.toFixed(1),
    }));
    setVideos(prev => [...prev, ...toAdd]);
    if (errors?.videos) setErrors(prev => ({ ...prev, videos: '' }));
    e.target.value = '';
  };

  const removeVideo = (idx) => {
    setVideos(prev => {
      const updated = [...prev];
      updated?.splice(idx, 1);
      return updated;
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!form?.boughtFromFactory?.trim()) newErrors.boughtFromFactory = t('sellYourSugar.errorFactoryRequired');
    if (!form?.grade) newErrors.grade = t('sellYourSugar.errorGradeRequired');
    if (!form?.season?.trim()) newErrors.season = t('sellYourSugar.errorSeasonRequired');
    if (!form?.quantity || isNaN(form?.quantity) || Number(form?.quantity) <= 0) newErrors.quantity = t('sellYourSugar.errorQuantityRequired');
    if (!form?.offeredRate || isNaN(form?.offeredRate) || Number(form?.offeredRate) <= 0) newErrors.offeredRate = t('sellYourSugar.errorRateRequired');
    if (!form?.loadingPincode || !/^\d{6}$/?.test(form?.loadingPincode)) newErrors.loadingPincode = 'Please enter a valid 6-digit loading pin code.';
    if (!form?.paymentByDate) newErrors.paymentByDate = t('sellYourSugar.errorPaymentDateRequired');
    if (!form?.liftingByDate) newErrors.liftingByDate = t('sellYourSugar.errorLiftingDateRequired');
    if (!form?.offerValidity || isNaN(form?.offerValidity) || Number(form?.offerValidity) < 1 || Number(form?.offerValidity) > 30) newErrors.offerValidity = t('sellYourSugar.errorValidityRequired');
    if (!form?.qualityRemark) newErrors.qualityRemark = t('sellYourSugar.errorQualityRequired');
    if (photos?.length === 0) newErrors.photos = t('sellYourSugar.errorPhotosRequired');
    if (videos?.length === 0) newErrors.videos = t('sellYourSugar.errorVideosRequired');
    if (!form?.contactMobile?.trim()) newErrors.contactMobile = t('sellYourSugar.errorContactRequired');
    if (!termsAccepted) newErrors.termsAccepted = 'You must accept the Terms & Conditions to proceed.';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // If guest user, show login/register prompt instead of submitting
    if (!user) {
      setShowGuestSubmitPrompt(true);
      return;
    }

    const newErrors = validate();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      const firstKey = Object.keys(newErrors)?.[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const sellerEmail = user?.email || userProfile?.email || '';
    const sellerName = userProfile?.name || userProfile?.full_name || user?.email || 'Seller';
    const offerId = `SHO-${Date.now()}`;

    // Upload photos to Supabase Storage
    const uploadedPhotoUrls = [];
    for (let i = 0; i < photos?.length; i++) {
      const photo = photos?.[i];
      if (photo?.file) {
        const ext = photo?.file?.name?.split('.')?.pop() || 'jpg';
        const path = `${offerId}/photos/photo_${i + 1}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('sell-offer-media')
          ?.upload(path, photo?.file, { upsert: true });
        if (!uploadError && uploadData) {
          const { data: urlData } = supabase?.storage
            ?.from('sell-offer-media')
            ?.getPublicUrl(path);
          if (urlData?.publicUrl) uploadedPhotoUrls?.push(urlData?.publicUrl);
        }
      }
    }

    // Upload videos to Supabase Storage
    const uploadedVideoUrls = [];
    for (let i = 0; i < videos?.length; i++) {
      const video = videos?.[i];
      if (video?.file) {
        const ext = video?.file?.name?.split('.')?.pop() || 'mp4';
        const path = `${offerId}/videos/video_${i + 1}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('sell-offer-media')
          ?.upload(path, video?.file, { upsert: true });
        if (!uploadError && uploadData) {
          const { data: urlData } = supabase?.storage
            ?.from('sell-offer-media')
            ?.getPublicUrl(path);
          if (urlData?.publicUrl) uploadedVideoUrls?.push(urlData?.publicUrl);
        }
      }
    }

    // Insert into quotas table so it appears in Quota Manager → Sell Offers → New Offers tab
    const { error: insertError } = await supabase?.from('quotas')?.insert({
      offer_id: offerId,
      factory_name: form?.boughtFromFactory,
      factory_short_code: form?.boughtFromFactory?.slice(0, 6)?.toUpperCase()?.replace(/\s/g, ''),
      grade: form?.grade,
      quantity: parseFloat(form?.quantity) || 0,
      price_per_ton: parseFloat(form?.offeredRate) || 0,
      availability_status: 'reserved',
      available_from_date: form?.paymentByDate || null,
      offer_valid_until: form?.liftingByDate || null,
      seller_name: sellerName,
      seller_mobile: form?.contactMobile || '',
      seller_email: sellerEmail,
      quality_remarks: form?.qualityRemark || '',
      season: form?.season || '',
      is_resume_request: false,
      photo_urls: uploadedPhotoUrls,
      video_urls: uploadedVideoUrls,
      loading_pincode: form?.loadingPincode || null,
    });

    if (insertError) {
      console.error('Failed to submit sell offer:', insertError);
      setErrors({ submit: 'Failed to submit your offer. Please try again.' });
      return;
    }

    // Send confirmation email to seller
    if (sellerEmail) {
      sendSellOfferEmail('submission', sellerEmail, {
        id: offerId,
        sellerName,
        factoryName: form?.boughtFromFactory,
        grade: form?.grade,
        quantity: form?.quantity,
        offeredRate: form?.offeredRate,
        paymentByDate: form?.paymentByDate,
        liftingByDate: form?.liftingByDate,
      });
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ background: '#faf7f2' }}>
        <RoleBasedHeader />
        <main className="main-content">
          <BreadcrumbNavigation />
          <div className="max-w-lg mx-auto px-4 py-16 text-center">
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-10">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: '#fef3c7' }}>
                <Icon name="CheckCircle" size={32} color="#d97706" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('sellYourSugar.offerSubmittedTitle')}</h2>
              <p className="text-gray-500 mb-6 text-sm leading-relaxed">
                {t('sellYourSugar.offerSubmittedDesc')}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate('/buyer-operations-dashboard', { state: { activeTab: 'pending' } })}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white w-full"
                  style={{ background: '#d97706' }}
                >
                  <Icon name="ClipboardList" size={16} />
                  Go to Pending Orders
                </button>
                <button
                  onClick={() => navigate('/buyer-operations-dashboard')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold border w-full"
                  style={{ borderColor: '#d97706', color: '#d97706', background: '#fffbeb' }}
                >
                  <Icon name="LayoutDashboard" size={16} />
                  Buyer Dashboard
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const inputClass = (name) =>
    `w-full px-3 py-2.5 rounded-lg border text-sm text-gray-800 bg-white outline-none transition-all focus:ring-2 focus:ring-amber-300 ${
      errors?.[name] ? 'border-red-400' : 'border-amber-200 focus:border-amber-400'
    }`;

  const labelClass = "block text-sm font-bold mb-1 uppercase tracking-wide" + " " + "text-[#1a1a1a]";

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      <Helmet>
        <title>Sell Your Sugar — Get Buyer Offers | SugarHathi</title>
        <meta name="description" content="List your sugar for sale on SugarHathi and connect with verified buyers across India." />
      </Helmet>
      <RoleBasedHeader />
      <main className="main-content">
        <BreadcrumbNavigation />

        {/* Guest Submit Prompt Modal */}
        {showGuestSubmitPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#fef3c7' }}>
                <Icon name="LogIn" size={26} color="#d97706" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">{t('marketplace.loginRequired', 'Login Required')}</h3>
              <p className="text-sm text-gray-500 mb-5 leading-relaxed">
                {t('sellYourSugar.loginToSubmit', 'Please login or register to complete your sugar selling submission')}
              </p>
              <div className="flex gap-3 mb-3">
                <button
                  onClick={() => navigate('/login-authentication')}
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-white"
                  style={{ background: '#d97706' }}
                >
                  {t('common.login')}
                </button>
                <button
                  onClick={() => navigate('/user-registration')}
                  className="flex-1 py-3 rounded-xl border text-sm font-bold"
                  style={{ borderColor: '#d97706', color: '#d97706', background: '#fffbeb' }}
                >
                  {t('common.register')}
                </button>
              </div>
              <button
                onClick={() => setShowGuestSubmitPrompt(false)}
                className="text-xs text-gray-400 underline"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        <div className="max-w-2xl mx-auto px-4 py-4">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#fef3c7' }}>
                <Icon name="Tag" size={16} color="#d97706" />
              </div>
              <h1 className="text-xl font-bold text-gray-800">{t('sellYourSugar.pageTitle')}</h1>
            </div>
            <p className="text-xs text-gray-500 ml-10">{t('sellYourSugar.pageSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Card: Factory Details */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                <Icon name="Building2" size={15} color="#d97706" /> {t('sellYourSugar.factoryDetails')}
              </h3>

              {/* Bought From Which Factory */}
              <div className="mb-4" id="field-boughtFromFactory">
                <label className={labelClass}>{t('sellYourSugar.boughtFromFactory')} <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="boughtFromFactory"
                  value={form?.boughtFromFactory}
                  onChange={handleChange}
                  placeholder={t('sellYourSugar.boughtFromFactoryPlaceholder')}
                  className={inputClass('boughtFromFactory')}
                />
                {errors?.boughtFromFactory && <p className="text-xs text-red-500 mt-1">{errors?.boughtFromFactory}</p>}
              </div>

              {/* Factory Location */}
              <div className="mb-4" id="field-factoryLocation">
                <label className={labelClass}>{t('sellYourSugar.factoryLocation')} <span className="text-gray-400 font-normal normal-case">{t('sellYourSugar.factoryLocationOptional')}</span></label>
                <input
                  type="text"
                  name="factoryLocation"
                  value={form?.factoryLocation}
                  onChange={handleChange}
                  placeholder={t('sellYourSugar.factoryLocationPlaceholder')}
                  className={inputClass('factoryLocation')}
                />
              </div>

              {/* Loading Pin Code */}
              <div className="mb-4" id="field-loadingPincode">
                <label className={labelClass}>Loading Pin Code <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  name="loadingPincode"
                  value={form?.loadingPincode}
                  onChange={(e) => {
                    const val = e?.target?.value?.replace(/\D/g, '')?.slice(0, 6);
                    setForm(prev => ({ ...prev, loadingPincode: val }));
                    if (errors?.loadingPincode) setErrors(prev => ({ ...prev, loadingPincode: '' }));
                  }}
                  placeholder="e.g. 416416"
                  maxLength={6}
                  inputMode="numeric"
                  className={inputClass('loadingPincode')}
                />
                <p className="text-[11px] text-gray-400 mt-0.5">6-digit pin code of the pickup / loading location</p>
                {errors?.loadingPincode && <p className="text-xs text-red-500 mt-1">{errors?.loadingPincode}</p>}
              </div>
            </div>

            {/* Card: Sugar Details */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                <Icon name="Package" size={15} color="#d97706" /> {t('sellYourSugar.sugarDetails')}
              </h3>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Grade */}
                <div id="field-grade">
                  <label className={labelClass}>{t('sellYourSugar.grade')} <span className="text-red-500">*</span></label>
                  <select
                    name="grade"
                    value={form?.grade}
                    onChange={handleChange}
                    className={inputClass('grade')}
                  >
                    {GRADE_OPTIONS?.map(o => (
                      <option key={o?.value} value={o?.value}>{o?.label}</option>
                    ))}
                  </select>
                  {errors?.grade && <p className="text-xs text-red-500 mt-1">{errors?.grade}</p>}
                </div>

                {/* Season */}
                <div id="field-season">
                  <label className={labelClass}>{t('sellYourSugar.season')} <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="season"
                    value={form?.season}
                    onChange={handleChange}
                    placeholder={t('sellYourSugar.seasonPlaceholder')}
                    className={inputClass('season')}
                  />
                  {errors?.season && <p className="text-xs text-red-500 mt-1">{errors?.season}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Quantity */}
                <div id="field-quantity">
                  <label className={labelClass}>{t('sellYourSugar.quantityMT')} <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    name="quantity"
                    value={form?.quantity}
                    onChange={handleChange}
                    placeholder={t('sellYourSugar.quantityPlaceholder')}
                    min="1"
                    className={inputClass('quantity')}
                  />
                  {errors?.quantity && <p className="text-xs text-red-500 mt-1">{errors?.quantity}</p>}
                </div>

                {/* Offered Rate */}
                <div id="field-offeredRate">
                  <label className={labelClass}>{t('sellYourSugar.offeredRateTon')} <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    name="offeredRate"
                    value={form?.offeredRate}
                    onChange={handleChange}
                    placeholder={t('sellYourSugar.offeredRatePlaceholder')}
                    min="1"
                    className={inputClass('offeredRate')}
                  />
                  {errors?.offeredRate && <p className="text-xs text-red-500 mt-1">{errors?.offeredRate}</p>}
                  <p className="text-[11px] text-gray-400 mt-0.5">+ GST (as applicable)</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Payment By Date */}
                <div id="field-paymentByDate">
                  <label className={labelClass}>{t('sellYourSugar.paymentByDate')} <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="paymentByDate"
                    value={form?.paymentByDate}
                    onChange={handleChange}
                    className={inputClass('paymentByDate')}
                  />
                  {errors?.paymentByDate && <p className="text-xs text-red-500 mt-1">{errors?.paymentByDate}</p>}
                </div>

                {/* Lifting By Date */}
                <div id="field-liftingByDate">
                  <label className={labelClass}>{t('sellYourSugar.liftingByDate')} <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="liftingByDate"
                    value={form?.liftingByDate}
                    onChange={handleChange}
                    className={inputClass('liftingByDate')}
                  />
                  {errors?.liftingByDate && <p className="text-xs text-red-500 mt-1">{errors?.liftingByDate}</p>}
                </div>
              </div>

              {/* Offer Validity */}
              <div id="field-offerValidity">
                <label className={labelClass}>{t('sellYourSugar.offerValidityDays')} <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  name="offerValidity"
                  value={form?.offerValidity}
                  onChange={handleChange}
                  placeholder={t('sellYourSugar.offerValidityPlaceholder')}
                  min="1"
                  max="30"
                  inputMode="numeric"
                  className={inputClass('offerValidity')}
                />
                <p className="text-[11px] text-gray-400 mt-0.5">{t('sellYourSugar.offerValidityHint')}</p>
                {errors?.offerValidity && <p className="text-xs text-red-500 mt-1">{errors?.offerValidity}</p>}
              </div>
            </div>

            {/* Card: Quality */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                <Icon name="Star" size={15} color="#d97706" /> {t('sellYourSugar.qualityRemarks')}
              </h3>

              <div className="mb-3" id="field-qualityRemark">
                <label className={labelClass}>{t('sellYourSugar.quality')} <span className="text-red-500">*</span></label>
                <select
                  name="qualityRemark"
                  value={form?.qualityRemark}
                  onChange={handleChange}
                  className={inputClass('qualityRemark')}
                >
                  {QUALITY_OPTIONS?.map(o => (
                    <option key={o?.value} value={o?.value}>{o?.label}</option>
                  ))}
                </select>
                {errors?.qualityRemark && <p className="text-xs text-red-500 mt-1">{errors?.qualityRemark}</p>}
              </div>

              <div>
                <label className={labelClass}>{t('sellYourSugar.additionalQualityNotes')} <span className="text-gray-400 font-normal normal-case">{t('sellYourSugar.additionalQualityNotesOptional')}</span></label>
                <textarea
                  name="qualityNote"
                  value={form?.qualityNote}
                  onChange={handleChange}
                  placeholder={t('sellYourSugar.additionalQualityNotesPlaceholder')}
                  rows={2}
                  className={`${inputClass('qualityNote')} resize-none`}
                />
              </div>
            </div>

            {/* Card: Photos */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                <Icon name="Camera" size={15} color="#d97706" /> {t('sellYourSugar.photos')}
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                {t('sellYourSugar.photosHint')}
              </p>

              <div id="field-photos" className="flex flex-wrap gap-3">
                {photos?.map((photo, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-amber-200 group">
                    <img src={photo?.preview} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Icon name="X" size={10} />
                    </button>
                    {idx === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 bg-amber-600/80 text-white text-[9px] text-center py-0.5 font-semibold">{t('sellYourSugar.ppBag')}</div>
                    )}
                  </div>
                ))}

                {photos?.length < 3 && (
                  <button
                    type="button"
                    onClick={() => photoInputRef?.current?.click()}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-amber-300 flex flex-col items-center justify-center gap-1 text-amber-500 hover:bg-amber-50 transition-colors"
                  >
                    <Icon name="Plus" size={18} />
                    <span className="text-[10px] font-medium">{photos?.length === 0 ? t('sellYourSugar.ppBag') : t('sellYourSugar.add')}</span>
                  </button>
                )}

                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoAdd}
                />
              </div>
              {errors?.photos && <p className="text-xs text-red-500 mt-2">{errors?.photos}</p>}
            </div>

            {/* Card: Video */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                <Icon name="Video" size={15} color="#d97706" /> {t('sellYourSugar.video')}
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                {t('sellYourSugar.videoHint')}
              </p>

              <div id="field-videos" className="space-y-2">
                {videos?.map((video, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#fef3c7' }}>
                      <Icon name="Film" size={15} color="#d97706" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{video?.name}</p>
                      <p className="text-[10px] text-gray-400">{video?.size} MB</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeVideo(idx)}
                      className="w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200 transition-colors flex-shrink-0"
                    >
                      <Icon name="X" size={11} />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => videoInputRef?.current?.click()}
                  className="w-full py-2.5 rounded-lg border-2 border-dashed border-amber-300 flex items-center justify-center gap-2 text-amber-600 text-xs font-medium hover:bg-amber-50 transition-colors"
                >
                  <Icon name="Plus" size={14} />
                  {videos?.length === 0 ? t('sellYourSugar.uploadVideoMandatory') : t('sellYourSugar.addAnotherVideo')}
                </button>

                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={handleVideoAdd}
                />
              </div>
              {errors?.videos && <p className="text-xs text-red-500 mt-2">{errors?.videos}</p>}
            </div>

            {/* Card: Contact */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                <Icon name="Phone" size={13} color="#d97706" /> {t('sellYourSugar.contact')}
              </h3>
              <div id="field-contactMobile">
                <label className={labelClass}>{t('sellYourSugar.contactMobile')} <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="contactMobile"
                  value={form?.contactMobile}
                  onChange={handleChange}
                  placeholder={t('sellYourSugar.contactMobilePlaceholder')}
                  maxLength={10}
                  inputMode="numeric"
                  className={inputClass('contactMobile')}
                />
                {errors?.contactMobile && <p className="text-xs text-red-500 mt-1">{errors?.contactMobile}</p>}
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 mb-4">
              <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                <Icon name="FileText" size={13} color="#d97706" /> {t('sellYourSugar.termsAndConditions')}
              </h3>
              <label className="flex items-start gap-3 cursor-pointer" id="field-termsAccepted">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e?.target?.checked);
                    if (errors?.termsAccepted) setErrors(prev => ({ ...prev, termsAccepted: '' }));
                  }}
                  className="mt-0.5 w-4 h-4 accent-amber-600 flex-shrink-0 cursor-pointer"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  {t('sellYourSugar.iAgreeToThe', 'I have read and agree to the')}{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-amber-700 font-semibold underline underline-offset-2 hover:text-amber-900 transition-colors"
                  >
                    {t('sellYourSugar.termsAndConditions')}
                  </button>
                </span>
              </label>
              {errors?.termsAccepted && (
                <p className="text-xs text-red-500 mt-2">{errors?.termsAccepted}</p>
              )}
            </div>

            {/* Sale Commission Note */}
            <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 mb-5">
              <Icon name="Info" size={15} color="#d97706" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                <strong>Sale Commission:</strong> A mutually agreed commission shall be applicable on successful transactions.
              </p>
            </div>

            {/* Submit */}
            {(!user || !user?.id || !['factory_admin', 'admin'].includes(userProfile?.role)) ? null : (
              <button
                type="submit"
                disabled={!termsAccepted}
                className={`w-full py-3.5 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md transition-all duration-200 ${
                  termsAccepted
                    ? 'hover:shadow-lg cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                style={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' }}
              >
                <Icon name="Send" size={16} />
                {t('sellYourSugar.submitOfferForReview')}
              </button>
            )}
          </form>
        </div>
      </main>
      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowTermsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#fef3c7' }}>
                  <Icon name="FileText" size={14} color="#d97706" />
                </div>
                <h2 className="text-base font-bold text-gray-800">Terms &amp; Conditions for "Sell Your Sugar"</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto px-5 py-4 flex-1">
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                By submitting an offer, you agree to:
              </p>
              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">1</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Accuracy of Information</p>
                    <p className="text-xs text-gray-600 leading-relaxed">You are responsible for providing correct details. False information may lead to rejection and account restrictions.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">2</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Sale Commission</p>
                    <p className="text-xs text-gray-600 leading-relaxed">Sale Commission: Mutually Agreed. A mutually agreed commission shall be applicable on successful transactions.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">3</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Approval Process</p>
                    <p className="text-xs text-gray-600 leading-relaxed">All offers are subject to review and approval by Quota Manager. We reserve the right to reject any offer.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">4</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Withdrawal and Reschedule</p>
                    <p className="text-xs text-gray-600 leading-relaxed">You may withdraw your offer anytime. Resuming with changed dates will require fresh approval.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">5</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Sold Out Responsibility</p>
                    <p className="text-xs text-gray-600 leading-relaxed">If you sell the sugar offline, you must immediately withdraw the offer. Failure to do so may result in penalties.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">6</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Quality and Delivery</p>
                    <p className="text-xs text-gray-600 leading-relaxed">You are solely responsible for quality and timely delivery. SugarHathi is only a facilitator.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">7</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Right to Remove</p>
                    <p className="text-xs text-gray-600 leading-relaxed">We reserve the right to remove any listing if it violates rules.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">8</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">No Guarantee of Sale</p>
                    <p className="text-xs text-gray-600 leading-relaxed">Listing does not guarantee sale.</p>
                  </div>
                </li>
              </ol>
              <p className="text-xs text-gray-500 mt-5 pt-4 border-t border-amber-100 italic">
                By submitting, you accept these terms.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-4 border-t border-amber-100 flex gap-3">
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-amber-200 text-sm font-semibold text-gray-600 hover:bg-amber-50 transition-colors"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setTermsAccepted(true);
                  if (errors?.termsAccepted) setErrors(prev => ({ ...prev, termsAccepted: '' }));
                  setShowTermsModal(false);
                }}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
                style={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' }}
              >
                {t('sellYourSugar.iAgree', 'I Agree')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellYourSugar;
